import shuffle from 'lodash/shuffle';
import getChattingState from '@/services/book/exercise-states/chatting';
import getLessonTestState from '@/services/book/exercise-states/lesson-test';
import getLetsPracticeState from '@/services/book/exercise-states/lets-practice';
import getLifeStoryState from '@/services/book/exercise-states/life-story';
import getListeningReadingState from '@/services/book/exercise-states/listening-reading';
import getPhrasebookState from '@/services/book/exercise-states/phrasebook';
import getVocabularyState from '@/services/book/exercise-states/vocabulary';
import { removeCompleted, removeSingleCompleted } from '@/services/book/exercise-parsers';

import { ExerciseConverted } from '@/interfaces/class/exercises';
import { Training } from '@/interfaces/class/training';

const shuffleMethods : {
  [key: string]: Function;
} = {
  chatting: getChattingState,
  'lesson-test': getLessonTestState,
  'lets-practice': getLetsPracticeState,
  'life-story': getLifeStoryState,
  'listening-reading': getListeningReadingState,
  phrasebook: getPhrasebookState,
  vocabulary: getVocabularyState,
};

const exerciseStates : {
  [key: string]: {
    currentScreen?: string;
    isNeedResultSlide?: boolean;
    shuffleTrainings?: boolean;
    shuffleElements?: boolean;
    removeCompleted?: boolean;
    removeSingleCompleted?: boolean;
  };
} = {
  chatting: {
    isNeedResultSlide: true,
    shuffleElements: true,
  },
  'flash-card': {},
  'lesson-test': {
    isNeedResultSlide: true,
    shuffleElements: true,
  },
  'lets-practice': {
    shuffleTrainings: true,
    shuffleElements: true,
    removeCompleted: true,
  },
  'homework-review': {
    shuffleTrainings: true,
  },
  summary: {},
  'life-story': {
    shuffleElements: true,
  },
  'listening-reading': {
    shuffleElements: true,
    isNeedResultSlide: true,
  },
  'need-to-know': {},
  phrasebook: {
    currentScreen: 'list',
    isNeedResultSlide: true,
    shuffleTrainings: true,
    shuffleElements: true,
    removeSingleCompleted: true,
  },
  vocabulary: {
    isNeedResultSlide: true,
    shuffleTrainings: true,
    shuffleElements: true,
    currentScreen: 'list',
    removeCompleted: true,
  },
  'watch-it': {
    shuffleTrainings: true,
  },
  'write-it-up': {
    shuffleTrainings: true,
  },
};

const defaultExerciseState = {
  alias: '',
  currentScreen: '',
  currentSlide: 0,
  totalSlides: 0,
  shuffleTrainings: false,
  data: [],
  dataTraining: [],
  answers: {},
  resultData: [],
  isFinishedExercise: false,
  isFinishedTraining: false,
  isNeedResultSlide: false,
  nextExercise: {
    id: '',
    alias: '',
  },
  removeCompleted: false,
};

const getDefaultState = (exercise: ExerciseConverted) => {
  let defaultExercise = exercise;
  const exerciseState = exerciseStates[defaultExercise.alias] || {};

  defaultExercise.data = defaultExercise.data.map((el: Training) => ({
    ...el,
    allAllowedTrainingTypes: [...el.allowedTrainingTypes],
  }));
  defaultExercise.dataTraining = [...defaultExercise.data];

  if (exerciseState.removeCompleted) {
    defaultExercise.dataTraining = removeCompleted(defaultExercise.dataTraining);

    if (!defaultExercise.dataTraining.length) {
      defaultExercise.dataTraining = [...defaultExercise.data];
    }
  }

  if (exerciseState.removeSingleCompleted) {
    defaultExercise.dataTraining = removeSingleCompleted(defaultExercise.dataTraining);

    if (!defaultExercise.dataTraining.length) {
      defaultExercise.dataTraining = [...defaultExercise.data];
    }
  }

  if (exerciseState.shuffleTrainings) {
    defaultExercise.dataTraining = shuffle(defaultExercise.dataTraining);
  }

  if (exerciseState.shuffleElements) {
    defaultExercise = shuffleMethods[defaultExercise.alias](defaultExercise);
  }

  return {
    ...defaultExerciseState,
    ...exerciseStates[defaultExercise.alias],
    ...defaultExercise,
    totalSlides: defaultExercise.dataTraining.length,
  };
};

export {
  defaultExerciseState,
  getDefaultState,
};
